import React, {useEffect, useRef} from "react";
import clsx from "clsx";
import CloseModalButton from "./CloseModalButton";
import {getClassList} from "../Utility/siteConfig";
import ModalFadeScreen from "./ModalFadeScreen";
import { setPopupClosed, setPopupOpen} from "../lib/popupStatus";
import {getZIndex} from "../Utility/html";

/**
 * 2023-02 used by ErrorModal, HiddenContentContainer, DynamicCartModal...
 * 2026-07 added flex for vertical centering, and min-w-3/4
 *
 * @param id
 * @param classNames
 * @param handleClose
 * @param children
 * @param rest
 * @returns {JSX.Element}
 * @constructor
 */
export default function Modal(
  {
    id,
    className,    // for modal-inner.
    styles = {},    // explicit styles for modal-inner
    handleClose,
    children,
    contentNode,
  }) {

  const modalRef = useRef(null);
  const contentRef = useRef(null);

  // When we have a ref, see if there is any content to inject into it.
  useEffect(() => {
    if (!modalRef?.current) return;
    if (!contentRef?.current) return;

    modalRef.current.focus();

    // register popup as open
    const fullId = id || 'generic-modal';

    // inform the popup manager that this popup is open, to inhibit other popups like klaviyo
    setPopupOpen(fullId);

    if (contentNode) {
      contentRef.current.append(contentNode);

      // call this function after it becomes visible.
      if (typeof (contentNode.onShowModal) == 'function') {
        contentNode.onShowModal({modalRef, contentRef})
      }
    }

    // CLEANUP FUNCTION
    return () => {
      // inform the popup manager that this popup is closed
      setPopupClosed(fullId);
    }
  }, [modalRef?.current, contentRef?.current]);

  if (!handleClose) {
    console.error("Modal component called without handleClose");
    handleClose = (e) => {
      throw new Error("Modal component called without handleClose");
    }
  }

  function handleKeyDown(e) {
    if (e.keyCode === 27) {
      handleClose();
    }
  }

  className ||= getClassList({
    key: 'Modal',
    // has legacy CSS in tailwind components.css
    fallback: ["top-2 p-2 bg-white text-gray-400"],
  })

  // prepend the essential classes.
  // "modal-inner", in tailwind/components, this controls the sizing only.
  className = clsx('modal-inner relative lg:max-w-auto min-w-3/4 ', className);

  // wrapper must be fixed and high-zindex to prevent scrolling to top
  styles.zIndex ||=  getZIndex("modalFadeScreen", 500_000);
      //maxHeight: "90%", minWidth: "20%", minHeight: "20%",
      //maxWidth: "90%"

  return (
    <div ref={modalRef} id={id || ""}
      className="fixed inset-0 overflow-y-auto"
      style={styles}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <ModalFadeScreen handleClose={handleClose}>
        <div className={className} style={styles}
          onClick={(e) => e.stopPropagation()}>
          <CloseModalButton handleClose={handleClose}/>
          <div ref={contentRef}>{children}</div>
        </div>
      </ModalFadeScreen>
    </div>)
}


